import {
  FETCH_SHIPMENT_PROJECTS,
  FETCH_SHIPMENT,
  CLEAR_SHIPMENT,
  FETCH_SHIPMENT_FAILED,
  FETCH_SHIPMENT_EVENTS,
  FETCH_SHIPMENT_LOCATIONS,
  FETCH_SHIPMENT_DATE,
  FETCH_SHIPMENT_DELIVERY,
  FETCH_SHIPMENT_GOODS,
  FETCH_SHIPMENT_CONTAINERS,
  FETCH_SHIPMENT_CARGO,
  STOP_SHIPMENT,
  FETCH_SHIPMENT_NOTIFICATION_RECIPIENTS,
  FETCH_SHIPMENT_FILES,
  POST_SHIPMENT_FILES,
  DELETE_SHIPMENT_FILES,
  FETCH_SHIPMENT_METRICS_BY_IDENTIFIER,
  FETCH_SHIPMENT_AND_LOCATION_STARTED,
  FETCH_SHIPMENT_AND_LOCATION_SUCCEEDED,
  FETCH_EVENTS_AND_LOGS_STARTED,
  FETCH_SHIPMENT_METRICS_BY_IDENTIFIER_STARTED,
  FETCH_SHIPMENT_METRICS_BY_IDENTIFIER_COMPLETED,
  FETCH_SHIPMENT_DATA_STARTED,
  FETCH_SHIPMENT_DATA_SUCCEEDED,
  SET_SHIPMENT_DATA,
  UPDATE_SHIPMENT_EVENTS
} from '#action/types';
import colors from '#component/shipment/overview/colors.json';
import { whitelabel } from '#config/whitelabel';
import {
  formattedAddress,
  requestStatuses as statuses
} from '#helper';

const defaultState = {
  devices: [],
  projects: [],
  events: [],
  defaultDevices: [],
  // request lifecycle state; kept separate from the shipment's own `status`
  // field (100/200/300/400) that the API returns in the FETCH_SHIPMENT payload
  requestStatus: statuses.idle,
  eventStatus: statuses.idle,
  metricsLoadingStatus: {}
};

export default (state = defaultState, action) => {
  switch (action.type) {
    case FETCH_SHIPMENT_PROJECTS:
      const [project] = action.payload;
      const proj = project ?? {};

      return {
        ...state,
        project: {
          ...proj,
          value: proj?.id,
          label: proj?.title
        }
      };
    case FETCH_SHIPMENT_DATA_STARTED:
      return {
        ...state,
        requestStatus: statuses.loading
      };
    case FETCH_SHIPMENT:
      const lineColors = whitelabel.getConfig()?.chart?.line?.color ?? colors;
      const { defaultDevices } = state;
      const newDefaultDevices = action.payload.devices.map((d, i) => {
        const defaultDevice = defaultDevices.find(dd => dd.id === d.id) ?? {};
        const color = lineColors[i % lineColors.length];
        const alerts = action.payload.alerts?.filter(a => a.cargoId === d.cargoId) ?? [];
        return ({
          ...defaultDevice, color, alerts, ...d, showAlertIcon: true, alertOn: !!d.projectAlertId
        });
      });

      const payload = {
        ...action.payload,
        defaultDevices: newDefaultDevices
      };
      delete payload.devices;

      return { ...state, ...payload };
    case SET_SHIPMENT_DATA:
      return {
        ...state,
        ...action.payload,
        requestStatus: statuses.succeeded
      };
    case FETCH_SHIPMENT_AND_LOCATION_STARTED:
      return {
        ...state,
        requestStatus: statuses.loading
      };
    case FETCH_SHIPMENT_AND_LOCATION_SUCCEEDED:
      return {
        ...state,
        requestStatus: statuses.succeeded
      };
    case FETCH_SHIPMENT_FAILED:
      return { ...state, requestStatus: statuses.failed, error: action.error };
    case CLEAR_SHIPMENT:
      return defaultState;
    case FETCH_EVENTS_AND_LOGS_STARTED:
      return {
        ...state,
        eventStatus: statuses.loading
      };
    case FETCH_SHIPMENT_EVENTS:
      const { events = [], logs = [] } = action.payload;
      return {
        ...state,
        defaultDevices: state.defaultDevices.map(d => ({
          ...d,
          events: [
            events?.find(m => m.id === d.id) ?? [],
            ...logs?.filter(l => l.device?.id === d.id) ?? []
          ]
        })),
        events: logs.filter(log =>
          !log.device ||
          (log.device && !state.defaultDevices.find(d => d.id === log.device.id))),
        eventStatus: statuses.succeeded,
        allEvents: logs
      };
    case UPDATE_SHIPMENT_EVENTS:
      const { newEvent, acceptance: alertAcceptance } = action.payload;

      const updatedDefaultDevices = state.defaultDevices.map(defaultDevice => ({
        ...defaultDevice,
        events: defaultDevice.events.map(event => {
          if (event?.logId === newEvent.details.logId) {
            if (alertAcceptance) {
              return {
                ...event,
                acceptance: {
                  value: alertAcceptance.value
                }
              };
            }

            const { acceptance, ...rest } = event;
            return {
              ...rest
            };
          }
          return event;
        })
      }));

      const updatedEvents = state.events.map(event => {
        if (event.logId === newEvent.details.logId) {
          if (alertAcceptance) {
            return {
              ...event,
              acceptance: {
                value: alertAcceptance.value
              }
            };
          }

          const { acceptance, ...rest } = event;
          return {
            ...rest
          };
        }
        return event;
      });

      const updatedAllEvents = state.allEvents.map(event => {
        if (event.logId === newEvent.details.logId) {
          if (alertAcceptance) {
            return {
              ...event,
              acceptance: {
                value: alertAcceptance.value
              }
            };
          }

          const { acceptance, ...rest } = event;
          return {
            ...rest
          };
        }
        return event;
      });

      return {
        ...state,
        defaultDevices: updatedDefaultDevices,
        events: updatedEvents,
        allEvents: updatedAllEvents
      };
    case FETCH_SHIPMENT_LOCATIONS:
      return {
        ...state,
        defaultDevices: state.defaultDevices.length > 0 ? state.defaultDevices.map(d => ({
          ...d,
          locations:
            action.payload?.find(m => m.id === d.id)?.locations ?? d.locations
        })) : action.payload?.map(d => ({ ...d }))
      };
    case FETCH_SHIPMENT_METRICS_BY_IDENTIFIER:
      return {
        ...state,
        defaultDevices: state.defaultDevices.length > 0 ? state.defaultDevices.map(d => {
          const metrics = { ...d?.metrics ?? {} };
          metrics[action.identifier] = action.payload?.find(m => m.id === d.id)?.measurements ?? [];
          return ({
            ...d,
            metrics
          });
        }) :
          action.payload?.map(p => ({
            id: p.id,
            metrics: { [action.identifier]: p.measurements ?? [] }
          })) ?? []
      };
    case FETCH_SHIPMENT_METRICS_BY_IDENTIFIER_STARTED:
      return {
        ...state,
        metricsLoadingStatus: {
          ...state.metricsLoadingStatus,
          [action.payload]: true
        }
      };
    case FETCH_SHIPMENT_METRICS_BY_IDENTIFIER_COMPLETED:
      return {
        ...state,
        metricsLoadingStatus: {
          ...state.metricsLoadingStatus,
          [action.payload]: false
        }
      };
    case FETCH_SHIPMENT_DATE:
      return {
        ...state,
        // deduct 1 sec to give time. `getShipmentDevices` compares too fast
        beginDate: new Date(new Date() - 1000)
      };
    case FETCH_SHIPMENT_DELIVERY: {
      const delivery = action.payload.map(s => (
        s.address ? { ...s, formattedAddress: formattedAddress(s.address) } : s
      ));
      return {
        ...state,
        delivery,
        // TODO: remove `states` after shipment overview (only?) will use 'delivery'
        states: delivery
      };
    }
    case FETCH_SHIPMENT_GOODS:
      return {
        ...state,
        goods: action.payload
      };
    case FETCH_SHIPMENT_CONTAINERS:
      return {
        ...state,
        containers: action.payload
      };
    case FETCH_SHIPMENT_CARGO:
      return {
        ...state,
        cargo: action.payload
      };
    case STOP_SHIPMENT:
      return {
        ...state,
        ...action.payload
      };
    case FETCH_SHIPMENT_NOTIFICATION_RECIPIENTS:
      return {
        ...state,
        recipients: action.payload
      };
    case FETCH_SHIPMENT_FILES:
      return {
        ...state,
        files: action.payload
      };
    case POST_SHIPMENT_FILES:
      return {
        ...state,
        files: [...state?.files ?? [], ...action.payload]
      };
    case DELETE_SHIPMENT_FILES:
      return {
        ...state,
        files: state.files.filter(f => !action.payload.includes(f.id))
      };
    case FETCH_SHIPMENT_DATA_SUCCEEDED:
      return {
        ...state,
        requestStatus: statuses.succeeded
      };
    default:
      return state;
  }
};
